import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  OnChanges,
  OnInit,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { Employee } from '@qaroni-app/employee/types/employee';
import { QaroniBaseFormsDirective } from '@qaroni-core/directives/qaroni-base-forms/qaroni-base-forms.directive';

@Component({
  selector: 'qaroni-select-employee-form-field',
  templateUrl: './select-employee-form-field.component.html',
  styleUrl: './select-employee-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectEmployeeFormFieldComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SelectEmployeeFormFieldComponent
  extends QaroniBaseFormsDirective
  implements OnInit, OnChanges, ControlValueAccessor
{
  readonly employees = input<Employee[]>([]);
  readonly label = input<string>($localize`Empleados`);
  readonly multiple = input<boolean>(false);
  readonly subscriptSizing = input<'dynamic' | 'fixed'>('fixed');
  readonly hideSingleSelectionIndicator = input<boolean>(true);

  private readonly searchInput = viewChild(MatInput);

  public employeesControl = new FormControl<number | number[]>(null);
  public searchControl = new FormControl('');

  public matchingEmployees: Employee[] = [];
  public selectedEmployees: Employee[] = [];
  public hiddenEmployees: Employee[] = [];
  private searchMatches: Employee[] = [];

  private searchValueChanges$ = this.searchControl.valueChanges;
  private employeeValueChanges$ = this.employeesControl.valueChanges;

  ngOnInit(): void {
    this.subs.add(this.searchValueChanges$.subscribe(this.getSearchChanges));
    this.subs.add(
      this.employeeValueChanges$.subscribe(this.employeeValueChanges)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employees'].currentValue) {
      this.filterEmployees(this.searchControl.value);
    }
  }

  get searchTerm(): string {
    return (this.searchControl.value ?? '').trim();
  }

  get hasSearch(): boolean {
    return this.searchTerm.length > 0;
  }

  get selectedIds(): number[] {
    const value = this.employeesControl.value;

    if (value === null || value === undefined) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }

  private get selectedIdSet(): Set<number> {
    return new Set(this.selectedIds);
  }

  get filterNoResults() {
    return this.searchMatches.length === 0;
  }

  get status() {
    if (!this.multiple()) {
      return 'unchecked';
    }

    const currentSelection = this.selectedIdSet;
    const matchIds = this.searchMatches.map(e => e.employeeId);

    if (matchIds.length === 0) {
      return 'unchecked';
    }

    const selectedMatchesCount = matchIds.filter(id =>
      currentSelection.has(id)
    ).length;

    if (selectedMatchesCount === matchIds.length) {
      return 'checked';
    }

    if (selectedMatchesCount > 0) {
      return 'indeterminate';
    }

    return 'unchecked';
  }

  get textSelectAllOption(): string {
    const allSelected = this.status === 'checked';

    if (!this.hasSearch) {
      return allSelected
        ? $localize`Deseleccionar todos`
        : $localize`Seleccionar todos`;
    }

    const count = this.searchMatches.length;

    return allSelected
      ? $localize`Deseleccionar coincidencias (${count}:count:)`
      : $localize`Seleccionar coincidencias (${count}:count:)`;
  }

  get selectionSummary(): string {
    const selectedIds = this.selectedIdSet;
    const count = selectedIds.size;

    if (count === 0) {
      return '';
    }

    const first = (this.employees() ?? []).find(employee =>
      selectedIds.has(employee.employeeId)
    );

    if (!first) {
      return $localize`${count}:count: empleados seleccionados`;
    }

    const name = `${first.firstName} ${first.lastName}`;

    if (count === 1) {
      return name;
    }

    return $localize`${name}:name: y ${count - 1}:count: más`;
  }

  public onPanelOpened(): void {
    this.searchInput()?.focus();
  }

  public onPanelClosed(): void {
    this.onTouched();
    this.searchControl.setValue('');
  }

  public onChange: (value: number | number[]) => void = () => {};
  public onTouched = () => {};

  public writeValue(value: number | number[] | ''): void {
    if (value === '') {
      this.employeesControl.setValue(this.multiple() ? [] : null, {
        emitEvent: false,
      });
    } else if (value !== undefined && value !== null) {
      this.employeesControl.setValue(value, { emitEvent: false });
    } else {
      this.employeesControl.setValue(this.multiple() ? [] : null, {
        emitEvent: false,
      });
    }
  }

  public registerOnChange(fn: (value: number | number[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.employeesControl.disable();
      this.searchControl.disable();
    } else {
      this.employeesControl.enable();
      this.searchControl.enable();
    }
  }

  private filterEmployees(search: string | null) {
    const employees = this.employees() ?? [];
    const searchTerm = search?.trim().toLowerCase();

    if (!searchTerm) {
      this.matchingEmployees = employees;
      this.selectedEmployees = [];
      this.hiddenEmployees = [];
      this.searchMatches = employees;
      return;
    }

    const selectedIds = this.selectedIdSet;
    const selectedMatching: Employee[] = [];
    const selectedNotMatching: Employee[] = [];
    this.matchingEmployees = [];
    this.hiddenEmployees = [];

    for (const employee of employees) {
      const matchesSearch = (employee.firstName + ' ' + employee.lastName)
        .toLowerCase()
        .includes(searchTerm);
      const isSelected = selectedIds.has(employee.employeeId);

      if (matchesSearch && isSelected) {
        selectedMatching.push(employee);
      } else if (matchesSearch) {
        this.matchingEmployees.push(employee);
      } else if (isSelected) {
        selectedNotMatching.push(employee);
      } else {
        this.hiddenEmployees.push(employee);
      }
    }

    this.selectedEmployees = [...selectedMatching, ...selectedNotMatching];
    this.searchMatches = [...this.matchingEmployees, ...selectedMatching];
  }

  public toggleSelectAll() {
    if (!this.multiple()) {
      return;
    }
    const matchIds = new Set(this.searchMatches.map(e => e.employeeId));
    const currentSelection = this.selectedIdSet;

    const allMatchesAreSelected = [...matchIds].every(id =>
      currentSelection.has(id)
    );

    if (allMatchesAreSelected) {
      const newSelection = [...currentSelection].filter(
        id => !matchIds.has(id)
      );
      this.employeesControl.setValue(newSelection);
    } else {
      const newSelection = [...new Set([...currentSelection, ...matchIds])];
      this.employeesControl.setValue(newSelection);
    }
  }

  private getSearchChanges = (search: string): void => {
    this.filterEmployees(search);
  };

  private employeeValueChanges = (value: number | number[]): void => {
    this.onChange(value);
    this.onTouched();
  };
}
